import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import { productCategories } from '@/mocks/products';
import { articles } from '@/mocks/articles';
import { brandInfo, brandValues } from '@/mocks/brand';

const organizationJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '呦呦 YouYou',
  url: 'https://yooyoozone.github.io',
  description: brandInfo.description,
  foundingDate: '2020',
  address: {
    '@type': 'PostalAddress',
    addressLocality: '杭州',
    addressCountry: 'CN',
  },
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '400-888-9688',
    contactType: 'customer service',
  },
  sameAs: [],
};

const websiteJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '呦呦 YouYou',
  url: 'https://yooyoozone.github.io',
  description: brandInfo.description,
  inLanguage: 'zh-CN',
};

const webPageJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: '呦呦品牌官方首页',
  description: '呦呦，源自《诗经》的千年意境，传递自然纯粹的品牌理念。探索呦呦系列产品与服务。',
  url: 'https://yooyoozone.github.io/',
  isPartOf: { '@type': 'WebSite', name: '呦呦 YouYou', url: 'https://yooyoozone.github.io' },
};

const categoryImages: Record<string, string> = {
  skincare: 'https://readdy.ai/api/search-image?query=A%20minimalist%20product%20photography%20of%20natural%20skincare%20bottles%20with%20botanical%20elements%2C%20soft%20warm%20lighting%20on%20cream%20background%2C%20subtle%20oriental%20aesthetic%2C%20jade%20green%20and%20terracotta%20accents%2C%20clean%20composition%2C%20serene%20atmosphere%2C%20high-end%20styling&width=600&height=400&seq=youyou-cat-skincare&orientation=landscape',
  fragrance: 'https://readdy.ai/api/search-image?query=A%20minimalist%20product%20photography%20of%20handcrafted%20candles%20and%20diffusers%20with%20dried%20flowers%2C%20soft%20warm%20lighting%20on%20cream%20background%2C%20subtle%20oriental%20aesthetic%2C%20sage%20green%20accents%2C%20clean%20composition%2C%20serene%20zen%20atmosphere&width=600&height=400&seq=youyou-cat-fragrance&orientation=landscape',
  tea: 'https://readdy.ai/api/search-image?query=A%20minimalist%20product%20photography%20of%20loose%20leaf%20tea%20in%20ceramic%20bowls%20with%20tea%20leaves%20scattered%2C%20soft%20warm%20lighting%20on%20cream%20background%2C%20subtle%20oriental%20zen%20aesthetic%2C%20terracotta%20accents%2C%20clean%20composition&width=600&height=400&seq=youyou-cat-tea&orientation=landscape',
  home: 'https://readdy.ai/api/search-image?query=A%20minimalist%20product%20photography%20of%20ceramic%20teaware%20and%20handwoven%20bamboo%20objects%2C%20soft%20warm%20lighting%20on%20cream%20background%2C%20subtle%20oriental%20aesthetic%2C%20jade%20green%20accents%2C%20clean%20composition%2C%20zen%20simplicity&width=600&height=400&seq=youyou-cat-home&orientation=landscape',
};

const articleImages: Record<string, string> = {
  'article-01': 'https://readdy.ai/api/search-image?query=Elegant%20oriental%20aesthetic%20editorial%20photography%2C%20warm%20earth%20tones%2C%20minimal%20composition%20with%20natural%20textures%2C%20soft%20diffused%20lighting%2C%20calm%20poetic%20zen%20atmosphere%2C%20muted%20sage%20green%20and%20cream%20palette&width=600&height=400&seq=article-01&orientation=landscape',
  'article-02': 'https://readdy.ai/api/search-image?query=Oriental%20botanical%20editorial%20photography%20of%20fresh%20herbs%20and%20flowers%20on%20rice%20paper%2C%20warm%20amber%20tones%2C%20minimal%20composition%2C%20soft%20natural%20lighting%2C%20traditional%20Chinese%20medicine%20aesthetic&width=600&height=400&seq=article-02&orientation=landscape',
  'article-03': 'https://readdy.ai/api/search-image?query=Elegant%20tea%20ceremony%20editorial%20photography%2C%20steaming%20teacup%20on%20wooden%20table%2C%20warm%20golden%20lighting%2C%20minimal%20oriental%20aesthetic%2C%20serene%20atmosphere%2C%20soft%20cream%20and%20terracotta%20tones&width=600&height=400&seq=article-03&orientation=landscape',
  'article-04': 'https://readdy.ai/api/search-image?query=Minimalist%20oriental%20living%20space%20editorial%20photography%2C%20ceramic%20vase%20with%20single%20branch%2C%20warm%20natural%20lighting%2C%20textured%20cream%20walls%2C%20zen%20simplicity%2C%20calm%20poetic%20atmosphere&width=600&height=400&seq=article-04&orientation=landscape',
};

export default function Home() {
  const featuredArticles = articles.slice(0, 4);

  return (
    <>
      <SEOHead
        title="品牌官方首页"
        description="呦呦，源自《诗经》的千年意境，传递自然纯粹的品牌理念。探索呦呦系列产品与服务，感受东方美学与现代生活的完美融合，让每一天都充满呦呦之美。"
        canonicalUrl="https://yooyoozone.github.io/"
        jsonLd={[organizationJsonLd, websiteJsonLd, webPageJsonLd]}
      />
      <ScrollToTop />
      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20serene%20oriental%20landscape%20with%20misty%20mountains%2C%20a%20deer%20grazing%20in%20a%20lush%20green%20meadow%20at%20dawn%2C%20soft%20warm%20golden%20morning%20light%2C%20traditional%20Chinese%20ink%20painting%20aesthetic%2C%20minimalist%20ethereal%20atmosphere%2C%20jade%20green%20and%20warm%20cream%20palette%2C%20artistic%20dreamy%20poetic%20mood&width=1800&height=900&seq=youyou-hero-01&orientation=landscape"
            alt="呦呦品牌意境图——山间鹿鸣，东方自然之美"
            title="呦呦品牌 东方美学 自然生活"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/35" />
          <div className="relative z-10 flex flex-col items-center justify-center h-full w-full px-4 text-center">
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-semibold text-background-50 tracking-widest">
              呦呦
            </h1>
            <p className="mt-4 md:mt-6 text-sm md:text-lg text-background-100/90 max-w-xl leading-relaxed">
              {brandInfo.slogan}
            </p>
            <p className="mt-2 text-xs md:text-sm text-background-200/80">
              {brandInfo.tagline}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/categories"
                className="px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                探索产品系列
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 border border-background-50/60 text-background-50 rounded-full text-sm font-medium hover:bg-background-50/10 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                了解品牌故事
              </Link>
            </div>
          </div>
        </section>

        {/* Brand Philosophy */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950">
                品牌理念
              </h2>
              <p className="mt-3 text-sm text-foreground-500 max-w-lg mx-auto">
                以《诗经》为灵感之源，将千年东方智慧融入当代生活
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {brandValues.map((value, idx) => (
                <div key={idx} className="text-center p-6 md:p-8 rounded-lg bg-background-50 border border-background-200/60 hover:border-primary-200 transition-colors duration-300">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100">
                    <i className={`${value.icon} text-xl text-primary-600`} />
                  </div>
                  <h3 className="mt-4 font-heading text-base font-semibold text-foreground-900">
                    {value.title}
                  </h3>
                  <p className="mt-2 text-xs md:text-sm text-foreground-500 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Categories Overview */}
        <section className="py-16 md:py-24 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950">
                产品系列
              </h2>
              <p className="mt-3 text-sm text-foreground-500 max-w-lg mx-auto">
                四大系列，构筑完整的东方生活美学体系
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6" data-product-shop>
              {productCategories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/categories?tab=${cat.id}`}
                  className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/60 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative h-48 md:h-56 overflow-hidden">
                    <img
                      src={categoryImages[cat.id]}
                      alt={`${cat.name}产品系列`}
                      title={`${cat.name} 呦呦东方美学`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors">
                      {cat.name}
                    </h3>
                    <p className="mt-2 text-xs text-foreground-500 leading-relaxed">
                      {cat.description}
                    </p>
                    <span className="inline-block mt-3 text-xs text-primary-500 font-medium">
                      查看系列 &rarr;
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-14 gap-4">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950">
                  精选文章
                </h2>
                <p className="mt-2 text-sm text-foreground-500">
                  品味东方生活美学的深度内容
                </p>
              </div>
              <Link
                to="/articles"
                className="text-sm text-primary-500 font-medium hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                查看全部文章 &rarr;
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {featuredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/articles/${article.slug}`}
                  className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/60 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="h-44 md:h-48 overflow-hidden">
                    <img
                      src={articleImages[article.coverImageSeq]}
                      alt={article.title}
                      title={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 text-xs text-foreground-400 mb-2">
                      <span>{article.date}</span>
                      <span>·</span>
                      <span>{article.readTime}</span>
                    </div>
                    <h3 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">
                      {article.title}
                    </h3>
                    <p className="mt-2 text-xs text-foreground-500 leading-relaxed line-clamp-2">
                      {article.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Brand Story Teaser */}
        <section className="relative py-16 md:py-24 bg-background-800 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img
              src="https://readdy.ai/api/search-image?query=A%20dreamy%20traditional%20Chinese%20ink%20wash%20painting%20texture%2C%20subtle%20soft%20brush%20strokes%2C%20misty%20mountain%20silhouettes%2C%20negative%20space%20composition%2C%20very%20light%20warm%20beige%20and%20cream%20tones%2C%20minimal%20abstract%20oriental%20art%2C%20textured%20rice%20paper%20background&width=1800&height=900&seq=youyou-story-bg&orientation=landscape"
              alt=""
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-background-50">
              呦呦鹿鸣，食野之苹
            </h2>
            <p className="mt-6 text-sm md:text-base text-background-200/90 leading-relaxed">
              {brandInfo.description}
            </p>
            <Link
              to="/about"
              className="inline-block mt-8 px-8 py-3 border border-background-200/60 text-background-100 rounded-full text-sm font-medium hover:bg-background-50/10 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              深入了解呦呦
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}